/* eslint-disable array-callback-return */
// REACT IMPORTS
import { useEffect, useMemo, useState } from "react";

// COMPONENT IMPORTS
import { paymentLogoGet } from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Skeleton from "react-loading-skeleton";

// ICONS IMPORTS
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { VisaIcon } from "icons/payments/VisaIcon";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
import { useUserData } from "store/slices/authSlice";
import QuestionsFooter from "components/auth/QuestionsFooter";
import { InfoIcon } from "icons/payments/InfoIcon";
import { ExportIcon } from "icons/payments/ExportIcon";
import {
  changePaymentScheduleName,
  formatCurrency,
  getPaymentMethod,
  rowHelperText,
} from "utils/HelperFunctions";
import { useParams } from "react-router-dom";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { formatPaymentListDate } from "utils/DateFormat";
import { decode } from "utils/HelperFunctions";
import moment from "moment";
import { RootState } from "store/store";
import { getPaymentCustomPlan, useLeadData } from "store/slices/dashboardSlice";
import {
  getScheduleDisplayAmount,
  getTransactionFeeForPaymentMethod,
  normalizeScheduleFeesForPaymentMethod,
} from "utils/updatePaymentPlanUtils";

const getPaymentMethodCode = (method: string) => {
  switch (method) {
    case "Bank":
      return "ACH";
    case "PayPal":
      return "PP";
    case "Cash App Pay":
      return "CAPP";
    case "Amazon Pay":
      return "AMZP";
    case "Google Pay":
      return "GP";
    case "Apple Pay":
      return "AP";
    case "Wire":
      return "WTO";
    case "Card":
      return "CC";
    case "CC":
    case "ACH":
    case "PP":
    case "CAPP":
    case "AMZP":
    case "GP":
    case "AP":
    case "WTO":
      return method;
    default:
      return "CC";
  }
};

// PROPS TYPE
interface UpdatePaymentPlanPreviewProps {
  show: boolean;
  setShow: any;
  updateData: any;
  setHandleDrawerSize: (value: number) => void;
}

/* ------------------------------------- PAYMENT PREVIEW ------------------------------------- */

const UpdatePaymentPlanPreview = (props: UpdatePaymentPlanPreviewProps) => {
  const { show, setShow, updateData, setHandleDrawerSize } = props;

  const dispatch = useAppDispatch();
  const { paymentLogo } = useAppSelector((state: RootState) => state.payment);
  const { isDashPreview } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const [isLoading] = useState<boolean>(false);
  const [isSchedulePreviewLoading, setIsSchedulePreviewLoading] =
    useState<boolean>(false);
  const [schedulePreviewData, setSchedulePreviewData] = useState<any>({});
  const userData = useUserData();
  const accountLeadData = useLeadData();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const isScheduleOnlyPreview = updateData?.__previewMode === "schedule";

  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    }
  };

  useEffect(() => {
    if (!paymentLogo && clusterId && show) onGetLogo();
  }, [paymentLogo, clusterId]);

  useEffect(() => {
    if (!show) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onClose = () => {
    setShow(false);
    setHandleDrawerSize(0);
  };

  const handleGetSchedulePreview = async () => {
    setIsSchedulePreviewLoading(true);

    const transactions = updateData?.paymentPlan?.transactions || [];
    const paymentMethodCode = getPaymentMethodCode(updateData?.__previewMethod);
    const toPreviewRow = (itm: any, index: number) => ({
      amount: itm?.amount,
      scheduled_amount: getScheduleDisplayAmount(
        itm,
        index,
        updateData?.paymentPlan?.percentage_amounts || [],
        updateData?.paymentPlan?.transaction_type || "",
        transactions
      ),
      balance: itm?.balance,
      fee: getTransactionFeeForPaymentMethod(itm, paymentMethodCode),
      status: itm?.status,
      payment_date: itm?.billing_date || itm?.date || itm?.payment_date,
    });
    const previewRows = transactions.map(toPreviewRow);

    // Already-processed installments keep their stored fee, date and amount;
    // only pending installments are re-quoted against the selected method.
    const processedRows = previewRows.filter(
      (itm: any) => itm?.status && itm?.status !== "Pending"
    );
    const pendingRows = previewRows.filter(
      (itm: any) => !itm?.status || itm?.status === "Pending"
    );

    let recalculatedPending = pendingRows;
    try {
      if (pendingRows.length) {
        const pendingFeeSubtotal = pendingRows.reduce(
          (sum: number, row: any) => sum + (Number(row?.fee) || 0),
          0
        );
        const payload = {
          date_array: pendingRows.map((row: any) => ({
            amount: row?.amount,
            balance: row?.balance,
            fee: row?.fee,
            status: row?.status,
            payment_date: row?.payment_date,
          })),
          lead_id: accountLeadData?.lead_id,
          cluster_id: userData?.data?.accountData?.clusterid,
          balance: userData?.data?.accountData?.balance,
          schedule_amount: (
            updateData?.paymentPlan?.schedule_amount || "custom"
          )?.toLowerCase(),
          payment_method: paymentMethodCode,
          no_of_transaction: pendingRows.length,
          grand_total: updateData?.paymentPlan?.grand_total || "0.00",
          fee_subtotal: pendingFeeSubtotal.toFixed(2),
          amount_total: updateData?.paymentPlan?.amount_subtotal || "0.00",
          start_date:
            pendingRows[0]?.payment_date || updateData?.paymentPlan?.start_date,
          schedule_fld: updateData?.paymentPlan?.schedule_fld || "daily",
          amount: updateData?.paymentPlan?.amount || "0.00",
          transaction_type:
            updateData?.paymentPlan?.transaction_type || "amount",
          make_payment: true,
          isAmount: true,
          isDelete: false,
          state: userData?.data?.accountData?.state || "",
          custom_conv: "on",
        };

        const res: any = await dispatch(getPaymentCustomPlan(payload)).unwrap();
        if (res?.body?.success && Array.isArray(res?.body?.data?.date_array)) {
          recalculatedPending = res.body.data.date_array.map(
            (row: any, idx: number) => {
              const recalculatedRow = {
                ...pendingRows[idx],
                ...row,
                status: pendingRows[idx]?.status ?? "Pending",
                scheduled_amount: pendingRows[idx]?.scheduled_amount ?? 0,
              };

              return {
                ...recalculatedRow,
                fee: getTransactionFeeForPaymentMethod(
                  recalculatedRow,
                  paymentMethodCode
                ),
              };
            }
          );
        }
      }
    } catch (error) {
      console.log("handleGetSchedulePreview error =====", error);
    }

    const mergedRows = [...processedRows, ...recalculatedPending].sort(
      (a: any, b: any) => {
        const aTime = new Date(a?.payment_date).getTime();
        const bTime = new Date(b?.payment_date).getTime();
        if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
        return aTime - bTime;
      }
    );
    const feeSubtotal = mergedRows.reduce(
      (sum: number, row: any) => sum + (Number(row?.fee) || 0),
      0
    );
    const amountTotal = mergedRows.reduce(
      (sum: number, row: any) => sum + (Number(row?.scheduled_amount) || 0),
      0
    );

    setSchedulePreviewData({
      date_array: mergedRows,
      fee_subtotal: feeSubtotal.toFixed(2),
      amount_total: amountTotal.toFixed(2),
      grand_total: (amountTotal + feeSubtotal).toFixed(2),
    });
    setIsSchedulePreviewLoading(false);
  };

  useEffect(() => {
    if (show && isScheduleOnlyPreview) {
      handleGetSchedulePreview();
    }
  }, [show, isScheduleOnlyPreview, updateData]);

  const normalizedScheduleTransactions = useMemo(
    () =>
      normalizeScheduleFeesForPaymentMethod(
        updateData?.paymentPlan?.transactions || [],
        getPaymentMethodCode(updateData?.__previewMethod)
      ),
    [updateData]
  );

  const calculatedTotal = useMemo(() => {
    let total_fee = 0;
    let total_amount = 0;
    let remaining_payments = 0;

    const transactions = normalizedScheduleTransactions;
    transactions.forEach((item: any, index: number) => {
      total_fee +=
        Number(item?.cf_amount ?? item?.fee ?? item?.con_fee ?? 0) || 0;
      total_amount += getScheduleDisplayAmount(
        item,
        index,
        updateData?.paymentPlan?.percentage_amounts || [],
        updateData?.paymentPlan?.transaction_type || "",
        transactions
      );

      if (item?.status === "Pending") {
        remaining_payments += 1;
      }
    });

    const grand_total = total_fee + total_amount;

    return { total_fee, total_amount, grand_total, remaining_payments };
  }, [updateData, normalizedScheduleTransactions]);

  if (isScheduleOnlyPreview) {
    const scheduleRows = schedulePreviewData?.date_array || [];
    const hasFee = Number(schedulePreviewData?.fee_subtotal || 0) > 0;

    return (
      <div className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto lg:pt-[70px]">
        <div className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-10">
          <div className="flex items-center gap-2">
            <EyeIcon
              strokeColor="var(--color-txt-primary)"
              width="24"
              height="24"
            />
            <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
              Preview
            </div>
          </div>
          <div
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon />
          </div>
        </div>
        <div className="lg:px-10 px-5 py-4 flex items-center justify-between">
          <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
            Payment Schedule
          </div>
        </div>
        <div className="w-full lg:px-10 px-5 py-4 items-start justify-start gap-1 flex flex-col">
          <p className="w-full text-xs font-normal text-txtSecondary">AMOUNT</p>
          <p className="w-full text-2xl text-txtPrimary">
            {isSchedulePreviewLoading ? (
              <Skeleton width={120} />
            ) : (
              formatCurrency(schedulePreviewData?.grand_total) || "$0.00"
            )}
          </p>
        </div>
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
            <div className="flex flex-col items-start gap-5">
              <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                SCHEDULE
              </div>
              <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
              <div className="flex items-center w-full justify-between">
                <div
                  className={`${hasFee ? "w-1/4" : "w-1/3"} min-w-[35px] text-sm text-txtParagraph`}
                >
                  #
                </div>
                <div
                  className={`${hasFee ? "w-1/4" : "w-1/3"} min-w-[96px] text-sm text-txtParagraph`}
                >
                  Payment Date
                </div>
                {hasFee && (
                  <div className="w-[20%] min-w-[56px] text-sm text-txtParagraph text-center">
                    Fee
                  </div>
                )}
                <div
                  className={`${hasFee ? "w-[20%]" : "w-1/3"} min-w-[96px] text-sm text-txtParagraph text-end`}
                >
                  Amount
                </div>
              </div>
              <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
              {(isSchedulePreviewLoading ? [1, 2, 3] : scheduleRows).map(
                (x: any, idx: number) => (
                  <div
                    className="flex items-center w-full justify-between"
                    key={idx}
                  >
                    <div
                      className={`${hasFee ? "w-1/4" : "w-1/3"} min-w-[35px] text-sm text-txtPrimary`}
                    >
                      {idx + 1}
                      {rowHelperText(idx + 1)}
                    </div>
                    <div
                      className={`${hasFee ? "w-1/4" : "w-1/3"} min-w-[96px] text-sm text-txtPrimary`}
                    >
                      {isSchedulePreviewLoading ? (
                        <Skeleton width={80} />
                      ) : x?.payment_date || x?.billing_date ? (
                        moment(x?.payment_date || x?.billing_date).format(
                          "MM/DD/YYYY"
                        )
                      ) : (
                        "--"
                      )}
                    </div>
                    {hasFee && (
                      <div className="w-[20%] min-w-[56px] text-sm text-txtPrimary text-center">
                        {isSchedulePreviewLoading ? (
                          <Skeleton width={60} />
                        ) : (
                          formatCurrency(x?.fee || 0)
                        )}
                      </div>
                    )}
                    <div
                      className={`${hasFee ? "w-[20%]" : "w-1/3"} min-w-[96px] text-sm text-txtPrimary text-end`}
                    >
                      {isSchedulePreviewLoading ? (
                        <Skeleton width={70} />
                      ) : (
                        formatCurrency(x?.scheduled_amount || 0)
                      )}
                    </div>
                  </div>
                )
              )}
              <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
              <div className="flex items-center w-full justify-between min-w-[230px]">
                <div className="text-xs font-medium text-txtPrimary">
                  SUBTOTAL
                </div>
                {hasFee && (
                  <div className="w-[20%] text-center text-txtPrimary font-bold">
                    {formatCurrency(schedulePreviewData?.fee_subtotal) ||
                      "$0.00"}
                  </div>
                )}
                <div className="text-end text-txtPrimary font-bold">
                  {formatCurrency(schedulePreviewData?.amount_total) || "$0.00"}
                </div>
              </div>
              <div className="h-[1px] w-full bg-strokePrimary min-w-[230px]" />
              <div className="flex items-start justify-between w-full min-w-[230px]">
                <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                  Total
                </div>
                <div className="text-lg leading-normal font-bold text-txtPrimary">
                  {formatCurrency(schedulePreviewData?.grand_total) || "$0.00"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto lg:pt-[70px]">
      <div className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-10">
        <div className="flex items-center gap-2">
          <EyeIcon
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            Preview
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon />
          </div>
        </div>
      </div>
      <div
        className={`py-[16px] lg:px-10 px-5 flex items-center justify-between ${
          isDashPreview ? "pt-[86px]" : ""
        }`}
      >
        <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
          Custom Plan
        </div>
        <div className="flex items-center justify-end gap-[10px]">
          {isLoading ? (
            <Skeleton width={170} height={50} />
          ) : paymentLogo ? (
            <img
              src={paymentLogo}
              alt=""
              className="animate__animated animate__fadeIn w-[4/5]"
            />
          ) : (
            <img
              src={logo}
              alt=""
              className="animate__animated animate__fadeIn w-[4/5]"
            />
          )}
          {!paymentLogo && (
            <div className="text-purple font-medium text-xl leading-[30px]">
              CompanyName
            </div>
          )}
        </div>
      </div>
      <div className="py-2 lg:px-10 px-5 flex items-center justify-between flex-col gap-2">
        <div className="w-full text-start text-xl text-blue leading-[26.05px]">
          {isLoading ? (
            <Skeleton width={120} height={20} />
          ) : (
            <>
              {updateData?.customerData?.first_name
                ? updateData?.customerData?.first_name
                : "--"}{" "}
              {updateData?.customerData?.last_name &&
                updateData?.customerData?.last_name}
            </>
          )}
        </div>
        <div className="w-full py-2 flex justify-between">
          <div className="text-sm leading-[18.23px]">
            <span className="text-txtSecondary">Account ID</span>{" "}
            <span className="text-txtPrimary">
              {isLoading ? (
                <Skeleton width={60} />
              ) : (
                updateData?.customerData?.accountid
              )}
            </span>
          </div>
          <div className="text-sm leading-[18.23px]">
            <span className="text-txtSecondary">Balance</span>{" "}
            <span className="text-txtPrimary">
              {isLoading ? (
                <Skeleton width={60} />
              ) : (
                formatCurrency(updateData?.customerData?.balance) || "$0.00"
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 pb-2">
        <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
          <div className="flex flex-col items-start gap-5">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary ">
              Billed to
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary">
                  Account ID
                </div>
                <div className="font-normal text-sm leading-normal text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    updateData?.customerData?.accountid
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary ">
                  Customer
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={150} />
                  ) : (
                    `${updateData?.clientCustomerData?.billing_first_name || ""} ${
                      updateData?.clientCustomerData?.billing_last_name || ""
                    }`
                  )}
                </div>
              </div>
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex items-start justify-between w-full">
              <div className="font-normal text-sm leading-normal text-txtSecondary whitespace-nowrap">
                Billing address
              </div>
              <div className="font-normal text-sm leading-[150%] text-txtPrimary text-right max-w-[200px]">
                {isLoading ? (
                  <Skeleton width={80} height={14} />
                ) : (
                  updateData?.clientCustomerData?.billing_address1
                )}{" "}
                {!isLoading &&
                updateData?.clientCustomerData?.billing_address1 ? (
                  <br />
                ) : null}
                {isLoading ? (
                  <Skeleton width={80} height={14} />
                ) : (
                  updateData?.clientCustomerData?.billing_address2
                )}{" "}
                {!isLoading &&
                updateData?.clientCustomerData?.billing_address2 ? (
                  <br />
                ) : null}
                {isLoading ? (
                  <Skeleton width={130} height={14} />
                ) : (
                  updateData?.clientCustomerData?.billing_city
                )}
                {!isLoading && updateData?.clientCustomerData?.billing_city ? (
                  <br />
                ) : null}
                {isLoading
                  ? null
                  : `${updateData?.clientCustomerData?.billing_state || ""} ${
                      updateData?.clientCustomerData?.billing_zip || ""
                    }`}
                {!isLoading &&
                (updateData?.clientCustomerData?.billing_zip ||
                  updateData?.clientCustomerData?.billing_state) ? (
                  <br />
                ) : null}
                {isLoading
                  ? null
                  : updateData?.clientCustomerData?.billing_country}
                {!isLoading &&
                updateData?.clientCustomerData?.billing_country ? (
                  <br />
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-b border-strokePrimary rounded-b-lg">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
              Payment method
            </div>
            <div className="flex items-center gap-2 text-txtPrimary">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                <>
                  {updateData?.clientCustomerData?.payment_method
                    ? getPaymentMethod({
                        card_type: updateData?.clientCustomerData?.cc_type,
                        method: updateData?.clientCustomerData?.payment_method,
                        digits: updateData?.clientCustomerData?.cc_4digit,
                        account_number:
                          updateData?.clientCustomerData?.cc_4digit,
                        className: "!gap-[2px]",
                        textClassName: "!text-txtPrimary",
                        bank_name: updateData?.clientCustomerData?.bank_name,
                        isHidden: true,
                        rowId:"update-data"
                      })
                    : "--"}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 py-2">
        <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
          <div className="flex flex-col items-start gap-5">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
              Terms
            </div>
            <div className="h-[1px] w-full bg-strokeSecondary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                  Start date{" "}
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="start-date-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal">The date when the first payment installment is scheduled to be processed.</div>`}
                  >
                    <InfoIcon
                      id="start-date-anchor"
                      className="cursor-pointer"
                    />
                  </CustomTooltip>
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    updateData?.paymentPlan &&
                    normalizedScheduleTransactions.length > 0 &&
                    formatPaymentListDate(
                      normalizedScheduleTransactions[0]?.billing_date,
                      userData?.data?.accountData?.timezone,
                      "short"
                    )
                  )}
                </div>
              </div>
              <div className="h-[1px] w-full bg-strokeSecondary" />

              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                  Frequency{" "}
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="frequency-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal">Defines how often the installments will be processed.</div>`}
                  >
                    <div
                      id="frequency-anchor"
                      className="cursor-pointer pointer-events-auto"
                    >
                      <InfoIcon />
                    </div>
                  </CustomTooltip>
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    <>
                      {changePaymentScheduleName(
                        updateData?.paymentPlan?.schedule_fld
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="h-[1px] w-full bg-strokeSecondary" />

              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                  Number of transactions{" "}
                  <CustomTooltip
                    maxwidth="280px"
                    place="top"
                    id="transaction-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal">The total number of payment installments within this schedule.</div>`}
                  >
                    <div
                      id="transaction-anchor"
                      className="cursor-pointer pointer-events-auto"
                    >
                      <InfoIcon />
                    </div>
                  </CustomTooltip>
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right capitalize">
                  {isLoading ? (
                    <Skeleton width={50} />
                  ) : (
                    normalizedScheduleTransactions.length
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:px-6 px-4 py-5 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg">
          <div className="flex items-center justify-between w-full">
            <div className="text-xl leading-normal font-bold text-txtPrimary flex justify-center items-center gap-[5px]">
              <span className="uppercase">Total</span>{" "}
              <CustomTooltip
                maxwidth="430px"
                place="top"
                id="total-payments-anchor"
                content={`<div class="text-xs leading-[18px] font-normal">Total plan amount with convenience fee included.</div>`}
              >
                <div
                  id="total-payments-anchor"
                  className="cursor-pointer pointer-events-auto"
                >
                  <InfoIcon />
                </div>
              </CustomTooltip>
            </div>
            <div className="flex items-center gap-2 text-txtPrimary font-bold">
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                formatCurrency(calculatedTotal?.grand_total) || "$0.00"
              )}
            </div>
          </div>
        </div>
      </div>
      {normalizedScheduleTransactions.length && !isLoading ? (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="flex flex-col items-start gap-4 w-full">
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="flex flex-col items-start gap-5">
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    SCHEDULE
                  </div>
                  <div className="flex items-center gap-[10px] lg:w-fit w-full lg:justify-start justify-between lg:pt-0 pt-3 ">
                    <p className="text-xs leading-normal text-txtParagraph">
                      Frequency:{" "}
                      <span className="font-medium text-txtPrimary">
                        {changePaymentScheduleName(
                          updateData?.paymentPlan?.schedule_fld
                        ) || "--"}
                      </span>
                    </p>
                    <div className="w-[1px] h-[19px] bg-strokePrimary lg:block hidden" />
                    <p className="text-xs leading-normal text-txtParagraph">
                      Remaining payments: {/* change it */}
                      <span className="font-medium text-txtPrimary">
                        {calculatedTotal?.remaining_payments || "--"}
                      </span>
                    </p>
                  </div>
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    calculatedTotal?.total_fee > 0
                      ? "min-w-[344px]"
                      : "min-w-[300px]"
                  }`}
                />
                <div className="flex items-center w-full">
                  <div
                    className={`${
                      calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                    } min-w-[96px]  text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>
                  <div
                    className={`${
                      calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-center`}
                  >
                    Status
                  </div>
                  {calculatedTotal?.total_fee > 0 ? (
                    <div
                      className={`${
                        calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                      } min-w-[56px] text-sm text-txtParagraph leading-normal text-center`}
                    >
                      Fee
                    </div>
                  ) : null}
                  <div
                    className={`${
                      calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-end pr-2.5 md2:pr-0`}
                  >
                    Amount
                  </div>
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    calculatedTotal?.total_fee > 0
                      ? "min-w-[344px]"
                      : "min-w-[300px]"
                  }`}
                />
                {normalizedScheduleTransactions.length > 0
                  ? normalizedScheduleTransactions.map(
                      (x: any, index: number) => {
                        return (
                          <div className="flex items-center w-full" key={index}>
                            <div
                              className={`${
                                calculatedTotal?.total_fee > 0
                                  ? "w-1/4"
                                  : "w-1/3"
                              } min-w-[96px] text-sm text-txtParagraph leading-normal text-start flex gap-[6px] justify-start items-center`}
                            >
                              {x?.payment_date
                                ? moment(x?.payment_date).format("MM/DD/YYYY")
                                : x?.billing_date
                                  ? moment(x?.billing_date).format("MM/DD/YYYY")
                                  : "--"}
                              <span
                                className="cursor-pointer"
                                // onClick={() => {
                                //   setIsPaymentPreview(true);
                                //   setIsPaymentPreviewId(x.id);
                                // }}
                              >
                                {/* {x?.status === "Succeeded" && (
                                  <ExportIcon
                                    className="group"
                                    _className="group-hover:!fill-iconObscure"
                                  />
                                )} */}
                              </span>
                            </div>
                            <div
                              className={`${
                                calculatedTotal?.total_fee > 0
                                  ? "w-1/4"
                                  : "w-1/3"
                              } min-w-[96px] text-sm text-txtPrimary leading-normal flex justify-center`}
                            >
                              <div
                                className={`px-2 rounded-md py-[3px] w-max ${
                                  x.status === "Pending"
                                    ? "bg-[#FDB6000C] text-yellow"
                                    : x.status === "Approved"
                                      ? "bg-[#1678F20D] text-btnPrimaryActive"
                                      : x.status === "Declined" ||
                                          x.status === "Cancelled" ||
                                          x.status?.toLowerCase() ===
                                            "disputed lost"
                                        ? "bg-[#FF00000D] text-red"
                                        : x.status === "Voided"
                                          ? "bg-[#FDB6000D] text-yellow"
                                          : x.status === "Succeeded" ||
                                              x.status?.toLowerCase() ===
                                                "disputed won"
                                            ? "bg-[#23B3390D] text-green"
                                            : x.status === "RefundedPartial" ||
                                                x.status === "Refunded"
                                              ? "bg-[#1678F20D] text-blue"
                                              : x.status === "Disputed"
                                                ? "bg-[#4F576A0D] text-iconBright"
                                                : x.status === "Incomplete"
                                                  ? "bg-[#4F576A0D] text-iconBright "
                                                  : ""
                                }`}
                              >
                                {x.status &&
                                (x.status === "Declined" ||
                                  x.status === "Cancelled")
                                  ? "Canceled"
                                  : x?.status}
                              </div>
                            </div>
                            {calculatedTotal?.total_fee > 0 ? (
                              <div
                                className={`${
                                  calculatedTotal?.total_fee > 0
                                    ? "w-1/4"
                                    : "w-1/3"
                                } min-w-[56px] text-sm text-txtPrimary leading-normal text-center`}
                              >
                                {x?.cf_amount || x?.fee || x?.con_fee
                                  ? formatCurrency(
                                      x?.cf_amount ?? x?.fee ?? x?.con_fee
                                    )
                                  : "$0.00"}
                              </div>
                            ) : null}
                            <div
                              className={`${
                                calculatedTotal?.total_fee > 0
                                  ? "w-1/4"
                                  : "w-1/3"
                              } min-w-[96px] text-sm text-txtPrimary leading-normal text-end pr-2.5 md2:pr-0`}
                            >
                              {formatCurrency(
                                getScheduleDisplayAmount(
                                  x,
                                  index,
                                  updateData?.paymentPlan?.percentage_amounts ||
                                    [],
                                  updateData?.paymentPlan?.transaction_type ||
                                    "",
                                  normalizedScheduleTransactions
                                )
                              ) || "$0.00"}
                            </div>
                          </div>
                        );
                      }
                    )
                  : null}
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    calculatedTotal?.total_fee > 0
                      ? "min-w-[344px]"
                      : "min-w-[300px]"
                  }`}
                />
                <div
                  className={`flex items-center w-full ${
                    calculatedTotal?.total_fee > 0
                      ? "min-w-[344px]"
                      : "min-w-[288px]"
                  }`}
                >
                  <div
                    className={`${
                      calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                    } text-xs leading-normal text-txtPrimary`}
                  >
                    SUBTOTAL
                  </div>
                  <div
                    className={`${calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"}`}
                  ></div>
                  {calculatedTotal?.total_fee > 0 ? (
                    <div
                      className={`${
                        calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                      } text-center text-txtPrimary font-bold leading-normal pl-2.5 md2:pr-0`}
                    >
                      {formatCurrency(calculatedTotal?.total_fee) || "$0.00"}
                    </div>
                  ) : null}
                  <div
                    className={`${
                      calculatedTotal?.total_fee > 0 ? "w-1/4" : "w-1/3"
                    } text-end text-txtPrimary font-bold leading-normal mr-2.5 md2:mr-0`}
                  >
                    {formatCurrency(calculatedTotal?.total_amount) || "$0.00"}
                  </div>
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    calculatedTotal?.total_fee > 0
                      ? "min-w-[344px]"
                      : "min-w-[300px]"
                  }`}
                />
                <div
                  className={`flex items-start justify-between w-full ${
                    calculatedTotal?.total_fee > 0
                      ? "min-w-[344px]"
                      : "min-w-[288px]"
                  }`}
                >
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    Total
                  </div>
                  <div className="text-lg leading-normal font-bold text-txtPrimary pr-2.5 md2:pr-0">
                    {formatCurrency(calculatedTotal?.grand_total) || "$0.00"}
                  </div>
                </div>
              </div>
            </div>
            {updateData?.paymentPlan?.payment_method !== "PP" && (
              <div className="flex items-center gap-2 text-xs font-normal leading-normal text-txtParagraph ">
                <FilledInfoIcon />A convenience fee is added to each installment
                payment.
              </div>
            )}
          </div>
          {!isLoading && (
            <div className={`py-5 lg:px-10 px-5 bottom-0 static`}>
              <QuestionsFooter />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default UpdatePaymentPlanPreview;
